import { useMemo } from 'react';

export interface AnchorRect { x: number; y: number; w: number; h: number };

export interface AnchoredPosition {
  top: number;
  bottom?: number;
  left: number;
  maxHeight: number;
  placement: 'below' | 'above';
}

export const useAnchoredPosition = (
  anchor: AnchorRect | null,
  options?: { width?: number; gutter?: number; maxHeight?: number; align?: 'start' | 'center' | 'end'; estimatedHeight?: number }
): AnchoredPosition => {
  return useMemo(() => {
    if (!anchor) return { top: 0, left: 0, maxHeight: 0, placement: 'below' };
    const panelWidth = options?.width ?? 240;
    const gutter = options?.gutter ?? 12;
    const viewportW = typeof global !== 'undefined' && (global as any).window?.innerWidth ? (global as any).window.innerWidth : 1024;
    const viewportH = typeof global !== 'undefined' && (global as any).window?.innerHeight ? (global as any).window.innerHeight : 768;

    // Prefer placing below the anchor
    const desiredTop = anchor.y + anchor.h + 6;
    const availableBelow = viewportH - desiredTop - gutter;
    const computedMax = options?.maxHeight ?? 320;

    // Horizontal: preferred alignment then clamp
    const align = options?.align ?? 'start';
    let preferredLeft = anchor.x;
    if (align === 'center') {
      preferredLeft = anchor.x + (anchor.w - panelWidth) / 2;
    } else if (align === 'end') {
      preferredLeft = anchor.x + anchor.w - panelWidth;
    }
    const maxLeft = viewportW - panelWidth - gutter;
    let left = Math.max(gutter, Math.min(preferredLeft, maxLeft));

    const shouldFlip = availableBelow < 180;

    if (shouldFlip) {
      // Cap maxHeight at actual available space above the anchor.
      const availableAbove = anchor.y - gutter;
      const maxHeight = Math.min(computedMax, Math.max(60, availableAbove));
      // Legacy `top` (used by callers that read only `top`, e.g. tooltips with
      // a known estimatedHeight). Kept for backward compatibility.
      const estimatedHeight = options?.estimatedHeight;
      const heightForFlip = Math.min(computedMax, Math.max(estimatedHeight ?? maxHeight, 60));
      const top = Math.max(gutter, anchor.y - heightForFlip - 6);
      // `bottom` anchors the panel so its lower edge hugs the trigger,
      // independent of actual panel height. Preferred by AnchoredDropdown.
      const bottom = Math.max(gutter, viewportH - anchor.y + 6);
      return { top, bottom, left, maxHeight, placement: 'above' };
    }

    const maxHeight = Math.min(computedMax, Math.max(180, availableBelow));
    return { top: desiredTop, left, maxHeight, placement: 'below' };
  }, [anchor, options?.width, options?.gutter, options?.maxHeight, options?.align, options?.estimatedHeight]);
};


